import React from 'react';
import styles from './day-calendar.module.scss';
import { IEmployeeDaySchedule } from 'shared/api/booking/interfaces/calendar-day.type';
import { CalendarCardType } from '../types/business-card.type';
import { BookingCard } from './booking-card';
import { BreakCard } from './break-card';
import { MicroSlot } from './micro-slot';

const getSlotClassName = (type?: string): string => {
  switch (type) {
    case 'dayOff':
      return styles.calendar_column__time_slot_off;
    case 'vacation':
      return styles.calendar_column__time_slot_vacation;
    case 'administrative':
      return styles.calendar_column__time_slot_administrative;
    case 'sickLeave':
      return styles.calendar_column__time_slot_sick_leave;
    default:
      return styles.calendar_column__time_slot_outside;
  }
};

interface CalendarColumnProps {
  employee: IEmployeeDaySchedule;
  dayTimes: string[];
  hourSlotHeight: number;
  microSlotHeight: number;
  microSlotDuration: number;
  isTimeInRange: (time: string) => boolean;
  toMinutes: (time: string) => number;
  timeToMinutes: (time: string) => number;
  getSlotStartTime: (
    time: string,
    index: number,
    microSlotDuration: number
  ) => string;
  dragState: {
    item: {
      id: string;
      type: CalendarCardType;
      employeeId: string;
      originalTop: number;
      height: number;
      startedAt: string;
      finishedAt: string;
    };
    startY: number;
    currentDeltaMinutes: number;
    previewTop: number;
  } | null;
  setDragState: React.Dispatch<
    React.SetStateAction<{
      item: {
        id: string;
        type: CalendarCardType;
        employeeId: string;
        originalTop: number;
        height: number;
        startedAt: string;
        finishedAt: string;
      };
      startY: number;
      currentDeltaMinutes: number;
      previewTop: number;
    } | null>
  >;
  setIsDraggingItem: React.Dispatch<React.SetStateAction<boolean>>;
  didDrag: boolean;
  setDidDrag: React.Dispatch<React.SetStateAction<boolean>>;
  clearDragTimeout: () => void;
  cancelDragListenersRef: React.MutableRefObject<(() => void) | null>;
  dragTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  onBlockClick: (
    id: string,
    type: CalendarCardType,
    employeeId: string
  ) => void;
  setCreateModalData: React.Dispatch<
    React.SetStateAction<{
      startTime: string;
      duration: number;
      employeeId: string;
      date: Date;
    } | null>
  >;
  date: Date;
}

export const CalendarColumn: React.FC<CalendarColumnProps> = ({
  employee,
  dayTimes,
  hourSlotHeight,
  microSlotHeight,
  microSlotDuration,
  isTimeInRange,
  toMinutes,
  timeToMinutes,
  getSlotStartTime,
  dragState,
  setDragState,
  setIsDraggingItem,
  didDrag,
  setDidDrag,
  clearDragTimeout,
  cancelDragListenersRef,
  dragTimeoutRef,
  onBlockClick,
  setCreateModalData,
  date,
}) => {
  // Compute overlapping booking and break IDs with overlapIndex
  const overlappingBookingIds = new Set<string>();
  const overlappingBreakIds = new Set<string>();
  const bookingOverlapIndexMap = new Map<string, number>();
  const breakOverlapIndexMap = new Map<string, number>();

  const toMinutesForOverlap = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  // Collect all pairwise overlaps
  const allOverlapPairs: [string, string][] = [];
  const sameTypeBookingPairs: [string, string][] = [];
  const sameTypeBreakPairs: [string, string][] = [];

  // Booking vs Booking
  for (let i = 0; i < employee.bookings.length; i++) {
    const b1 = employee.bookings[i];
    const s1 = toMinutesForOverlap(b1.startedAt);
    const e1 = toMinutesForOverlap(b1.finishedAt);

    for (let j = i + 1; j < employee.bookings.length; j++) {
      const b2 = employee.bookings[j];
      const s2 = toMinutesForOverlap(b2.startedAt);
      const e2 = toMinutesForOverlap(b2.finishedAt);

      if (s1 < e2 && e1 > s2) {
        allOverlapPairs.push([b1.id, b2.id]);
        sameTypeBookingPairs.push([b1.id, b2.id]);
      }
    }
  }

  // Break vs Break
  for (let i = 0; i < employee.breaks.length; i++) {
    const br1 = employee.breaks[i];
    const s1 = toMinutesForOverlap(br1.startedAt);
    const e1 = toMinutesForOverlap(br1.finishedAt);

    for (let j = i + 1; j < employee.breaks.length; j++) {
      const br2 = employee.breaks[j];
      const s2 = toMinutesForOverlap(br2.startedAt);
      const e2 = toMinutesForOverlap(br2.finishedAt);

      if (s1 < e2 && e1 > s2) {
        allOverlapPairs.push([br1.id, br2.id]);
        sameTypeBreakPairs.push([br1.id, br2.id]);
      }
    }
  }

  // Booking vs Break
  for (const booking of employee.bookings) {
    const bStart = toMinutesForOverlap(booking.startedAt);
    const bEnd = toMinutesForOverlap(booking.finishedAt);

    for (const br of employee.breaks) {
      const brStart = toMinutesForOverlap(br.startedAt);
      const brEnd = toMinutesForOverlap(br.finishedAt);

      if (bStart < brEnd && bEnd > brStart) {
        allOverlapPairs.push([booking.id, br.id]);
      }
    }
  }

  // Build connected components using ALL overlap pairs (cross-type included)
  const allItems = [
    ...employee.bookings.map((b) => ({
      id: b.id,
      startedAt: b.startedAt,
    })),
    ...employee.breaks.map((br) => ({
      id: br.id,
      startedAt: br.startedAt,
    })),
  ];

  const parent = new Map<string, string>();
  const find = (x: string): string => {
    if (!parent.has(x)) parent.set(x, x);
    const px = parent.get(x);
    if (px !== x && px !== undefined) parent.set(x, find(px));
    return parent.get(x) ?? x;
  };
  const union = (a: string, b: string) => {
    parent.set(find(a), find(b));
  };

  for (const [a, b] of allOverlapPairs) {
    union(a, b);
  }

  const groups = new Map<string, string[]>();
  for (const item of allItems) {
    const root = find(item.id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)?.push(item.id);
  }

  const itemById = new Map(allItems.map((item) => [item.id, item]));

  // Build unified index map, then split by type
  const unifiedIndexMap = new Map<string, number>();
  Array.from(groups.values()).forEach((ids) => {
    if (ids.length < 2) return;
    const sorted = ids
      .map((id: string) => itemById.get(id))
      .filter(
        (item): item is { id: string; startedAt: string } => item !== undefined
      )
      .sort(
        (a: { startedAt: string }, b: { startedAt: string }) =>
          toMinutesForOverlap(a.startedAt) - toMinutesForOverlap(b.startedAt)
      );
    sorted.forEach((item: { id: string }, idx: number) => {
      unifiedIndexMap.set(item.id, idx);
    });
  });

  // Split unified map into booking and break maps
  const bookingIds = new Set(employee.bookings.map((b) => b.id));
  Array.from(unifiedIndexMap.entries()).forEach(([id, idx]) => {
    if (bookingIds.has(id)) {
      bookingOverlapIndexMap.set(id, idx);
    } else {
      breakOverlapIndexMap.set(id, idx);
    }
  });

  // Mark overlapping items for styling
  for (const [a, b] of allOverlapPairs) {
    if (bookingIds.has(a)) overlappingBookingIds.add(a);
    else overlappingBreakIds.add(a);
    if (bookingIds.has(b)) overlappingBookingIds.add(b);
    else overlappingBreakIds.add(b);
  }
  // Also mark same-type overlaps
  for (const [a, b] of sameTypeBookingPairs) {
    overlappingBookingIds.add(a);
    overlappingBookingIds.add(b);
  }
  for (const [a, b] of sameTypeBreakPairs) {
    overlappingBreakIds.add(a);
    overlappingBreakIds.add(b);
  }

  const workDayType = employee.workDay?.type;

  return (
    <div className={styles.employee__calendar_column}>
      {dayTimes.map((time) => {
        if (!isTimeInRange(time)) {
          return (
            <div
              key={time.split(':')[0]}
              className={getSlotClassName(workDayType)}
              style={{ height: `${hourSlotHeight}px` }}
            ></div>
          );
        }

        const hourStartMin = timeToMinutes(time);
        const hourEndMin = hourStartMin + 60;

        if (employee.workDay) {
          const { startedAt, finishedAt, type } = employee.workDay;

          if (!startedAt || !finishedAt || type === 'dayOff') {
            return (
              <div
                key={time.split(':')[0]}
                className={getSlotClassName(type)}
                style={{ height: `${hourSlotHeight}px` }}
              ></div>
            );
          }

          const workStartMin = timeToMinutes(startedAt);
          const workEndMin = timeToMinutes(finishedAt);
          const isEntireHourOutside =
            hourEndMin <= workStartMin || hourStartMin >= workEndMin;

          if (isEntireHourOutside) {
            return (
              <div
                key={time.split(':')[0]}
                className={getSlotClassName(type)}
                style={{ height: `${hourSlotHeight}px` }}
              ></div>
            );
          }
        } else {
          return (
            <div
              key={time.split(':')[0]}
              className={styles.calendar_column__time_slot_outside}
              style={{ height: `${hourSlotHeight}px` }}
            ></div>
          );
        }

        const msCount = 60 / microSlotDuration;

        return (
          <div
            key={time.split(':')[0]}
            className={styles.calendar_column__time_slot}
            style={{ height: `${hourSlotHeight}px` }}
          >
            {employee.bookings
              .filter((booking) => {
                const start = toMinutes(booking.startedAt);
                const slotStart = toMinutes(time);
                const slotEnd = slotStart + hourSlotHeight;
                return start >= slotStart && start < slotEnd;
              })
              .map((booking) => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  employeeId={employee.employee.id}
                  time={time}
                  isOverlapping={overlappingBookingIds.has(booking.id)}
                  overlapIndex={bookingOverlapIndexMap.get(booking.id)}
                  dragState={dragState}
                  setDragState={setDragState}
                  setIsDraggingItem={setIsDraggingItem}
                  didDrag={didDrag}
                  setDidDrag={setDidDrag}
                  toMinutes={toMinutes}
                  timeToMinutes={timeToMinutes}
                  hourSlotHeight={hourSlotHeight}
                  clearDragTimeout={clearDragTimeout}
                  cancelDragListenersRef={cancelDragListenersRef}
                  dragTimeoutRef={dragTimeoutRef}
                  onBlockClick={onBlockClick}
                />
              ))}
            {employee.breaks
              .filter((br) => {
                const start = toMinutes(br.startedAt);
                const slotStart = toMinutes(time);
                const slotEnd = slotStart + hourSlotHeight;
                return start >= slotStart && start < slotEnd;
              })
              .map((br) => (
                <BreakCard
                  key={br.id}
                  br={br}
                  employeeId={employee.employee.id}
                  time={time}
                  isOverlapping={overlappingBreakIds.has(br.id)}
                  overlapIndex={breakOverlapIndexMap.get(br.id)}
                  dragState={dragState}
                  setDragState={setDragState}
                  setIsDraggingItem={setIsDraggingItem}
                  didDrag={didDrag}
                  setDidDrag={setDidDrag}
                  toMinutes={toMinutes}
                  timeToMinutes={timeToMinutes}
                  hourSlotHeight={hourSlotHeight}
                  clearDragTimeout={clearDragTimeout}
                  cancelDragListenersRef={cancelDragListenersRef}
                  dragTimeoutRef={dragTimeoutRef}
                  onBlockClick={onBlockClick}
                />
              ))}

            {Array.from({ length: msCount }).map((_, index) => {
              const isLast = index === msCount - 1;
              const microslotStartMinutes =
                timeToMinutes(time) + index * microSlotDuration;
              const microslotEndMinutes =
                microslotStartMinutes + microSlotDuration;

              let isFullyOutside = false;
              let gradientStyle = {};

              if (employee.workDay?.startedAt && employee.workDay?.finishedAt) {
                const workStartMin = timeToMinutes(employee.workDay.startedAt);
                const workEndMin = timeToMinutes(employee.workDay.finishedAt);

                if (
                  microslotEndMinutes <= workStartMin ||
                  microslotStartMinutes >= workEndMin
                ) {
                  isFullyOutside = true;
                } else {
                  if (
                    microslotStartMinutes < workStartMin &&
                    microslotEndMinutes > workStartMin
                  ) {
                    const grayPercent =
                      ((workStartMin - microslotStartMinutes) /
                        microSlotDuration) *
                      100;
                    gradientStyle = {
                      background: `linear-gradient(to bottom, #eeeeee 0%, #eeeeee ${grayPercent}%, white ${grayPercent}%, white 100%)`,
                    };
                  } else if (
                    microslotStartMinutes < workEndMin &&
                    microslotEndMinutes > workEndMin
                  ) {
                    const grayPercent =
                      ((workEndMin - microslotStartMinutes) /
                        microSlotDuration) *
                      100;
                    gradientStyle = {
                      background: `linear-gradient(to bottom, white 0%, white ${grayPercent}%, #eeeeee ${grayPercent}%, #eeeeee 100%)`,
                    };
                  }
                }
              } else {
                isFullyOutside = true;
              }

              return (
                <MicroSlot
                  key={index}
                  index={index}
                  microSlotHeight={microSlotHeight}
                  isFullyOutside={isFullyOutside}
                  isLast={isLast}
                  gradientStyle={gradientStyle}
                  onClick={() => {
                    const slotStartTime = getSlotStartTime(
                      time,
                      index,
                      microSlotDuration
                    );
                    setCreateModalData({
                      startTime: slotStartTime,
                      duration: microSlotDuration,
                      employeeId: employee.employee.id,
                      date: date,
                    });
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
